import { getShape1D } from 'ml-peak-shape-generator';
import { xyFindClosestPoint } from 'ml-spectra-processing';
import { defaultParameters } from './defaultParameters';
import { signalsToPointXY } from './signalsToPointXY';
const properties = ['init', 'min', 'max', 'gradientDifference'];
export function getInternalSignals(data, signals, minMaxY, options) {
    let index = 0;
    let internalSignals = [];
    const { shape: shapeAsOption = { kind: 'gaussian' }, baseline: shiftValue = minMaxY.min, lineWidth = 1, frequency, } = options;
    const normalizedSignals = normalizeSignals({
        signals,
        data,
        shiftValue,
        frequency,
        range: minMaxY.range,
    });
    for (const signal of normalizedSignals) {
        const { shape = { ...shapeAsOption } } = signal;
        if (!shape.fwhm) {
            shape.fwhm = lineWidth;
        }
        shape.fwhm /= frequency;
        const shapeFct = getShape1D(shape);
        const parameters = [
            'delta',
            'intensity',
            ...shapeFct.getParameters(),
        ];
        const propertiesValues = {
            min: [],
            max: [],
            init: [],
            gradientDifference: [],
        };
        for (const parameter of parameters) {
            for (const property of properties) {
                propertiesValues[property].push(getPropertyValue({
                    signal,
                    frequency,
                    parameter,
                    property,
                    minMaxY,
                    shapeFct,
                }));
            }
        }
        for (const jCoupling of signal.js || []) {
            for (const property of properties) {
                const parameter = 'coupling';
                let generalParameterValue = options?.parameters?.[parameter]?.[property];
                if (generalParameterValue) {
                    if (typeof generalParameterValue === 'number') {
                        propertiesValues[property].push(getNormalizedValue(generalParameterValue, parameter, property, minMaxY, frequency, options.baseline));
                    }
                    else {
                        let value = generalParameterValue({ jCoupling });
                        propertiesValues[property].push(getNormalizedValue(value, parameter, property, minMaxY, frequency, options.baseline));
                    }
                }
                if (!defaultParameters[parameter]) {
                    throw new Error(`No default parameter for ${parameter}`);
                }
                const defaultParameterValues = defaultParameters[parameter][property];
                propertiesValues[property].push(defaultParameterValues({ jCoupling }));
            }
        }
        const fromIndex = index;
        const toIndex = fromIndex + parameters.length - 1;
        const fromIndexCoupling = toIndex + 1;
        const toIndexCoupling = fromIndexCoupling + signal.js.length - 1;
        index += toIndexCoupling - fromIndex + 1;
        internalSignals.push({
            shape: shape,
            shapeFct,
            signal,
            parameters,
            propertiesValues,
            fromIndex,
            toIndex,
            fromIndexCoupling,
            toIndexCoupling,
        });
    }
    return internalSignals;
}
function normalizeSignals(options) {
    const { signals, data, frequency, shiftValue, range } = options;
    const getMaxIntensity = (signal) => {
        const peaks = signalsToPointXY([signal], { frequency, maxClusterSize: 1 });
        const biggestPeak = peaks.reduce((biggest, peak) => (peak.y > biggest.y ? peak : biggest), peaks[0]);
        const { y: maxIntensity } = xyFindClosestPoint(data, biggestPeak.x);
        return maxIntensity;
    };
    return signals.map((signal) => {
        const { intensity = getMaxIntensity(signal) } = signal;
        return {
            ...signal,
            js: signal.js || [],
            intensity: (intensity - shiftValue) / range,
        };
    });
}
function getPropertyValue(options) {
    const { signal, frequency, parameter, property, minMaxY, shapeFct } = options;
    // check if the property is specified in the signal
    let propertyValue = signal?.parameters?.[parameter]?.[property];
    if (propertyValue) {
        return getNormalizedValue(propertyValue, parameter, property, minMaxY, frequency, options.baseline);
    }
    // check if there are some global option, it could be a number or a callback
    let generalParameterValue = options?.parameters?.[parameter]?.[property];
    if (generalParameterValue) {
        if (typeof generalParameterValue === 'number') {
            return getNormalizedValue(generalParameterValue, parameter, property, minMaxY, frequency, options.baseline);
        }
        else {
            let value = generalParameterValue(signal);
            return getNormalizedValue(value, parameter, property, minMaxY, frequency, options.baseline);
        }
    }
    // we just need to take the default parameters
    if (!defaultParameters[parameter]) {
        throw new Error(`No default parameter for ${parameter}`);
    }
    const defaultParameterValues = defaultParameters[parameter][property];
    return defaultParameterValues({ signal, shape: shapeFct });
}
function getNormalizedValue(value, parameter, property, minMaxY, frequency, baseline) {
    if (parameter === 'intensity') {
        if (property === 'gradientDifference') {
            return value;
        }
        else {
            return baseline !== undefined
                ? (value - baseline) / minMaxY.range
                : (value - minMaxY.min) / minMaxY.range;
        }
    }
    else if (parameter === 'fwhm') {
        return value / frequency;
    }
    return value;
}
//# sourceMappingURL=getInternalSignals.js.map